import { useState, useCallback } from 'react';
import { ApiUrl } from '../../../utils/ApiUrl';

const useChildPoolsAPI = () => {
    const [childPools, setChildPools] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 10,
        offset: 0
    });

    const fetchChildPools = useCallback(async (
        limit = 10,
        offset = 0,
        search = '',
        sort = 'created_desc'
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            // Get access token from localStorage
            const accessToken = localStorage.getItem('accessToken');

            // Prepare headers
            const headers = {
                'Content-Type': 'application/json',
            };

            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }

            // Build URL with pagination, search, and sort parameters
            let url = `${ApiUrl}/pool/my-child-pools?limit=${limit}&offset=${offset}`;

            // Add search parameter if provided
            if (search && search.trim()) {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }

            if (sort) {
                url += `&sort=${sort}`;
            }

            // Make API call
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch child pools: ${response.statusText}`);
            }

            const result = await response.json();
            // console.log('Child pools fetched successfully:', result);

            // Update pagination data
            if (result?.data?.pagination) {
                setPagination({
                    total: result.data.pagination.total,
                    limit: result.data.pagination.limit,
                    offset: result.data.pagination.offset
                });
            }

            // Transform API data to match component expectations
            const transformedData = result?.data?.items?.map((pool) => ({
                id: pool._id,
                rootPoolAddress: pool.rootPool,
                tagline: pool.tagline,
                description: pool.poolDescription,
                creatorFeePercent: pool.creatorFeePercent,
                stakingAmount: pool.stakingAmount,
                apyPercent: pool.apyPercent,
                creator: {
                    id: pool.createdBy._id,
                    walletAddress: pool.createdBy.walletAddress,
                    name: pool.createdBy.name,
                    email: pool.createdBy.email,
                    imageUrl: pool.createdBy.imageUrl,
                    description: pool.createdBy.description,
                    socialLinks: pool.createdBy.socialLinks,
                },
                rootPool: {
                    metadata: {
                        logo: pool?.rootPoolId?.projectLogo || '',
                        projectName: pool?.rootPoolId?.projectName || '',
                        tagline: pool?.rootPoolId?.tagline || '',
                        rewardToken: pool?.rootPoolId?.rewardToken || '',
                        ...pool?.rootPoolId

                    }
                },
                creatorFee: pool.creatorFeePercent * 100,
                blockTimestamp: new Date(pool.createdAt).getTime() / 1000,
                transactionHash: pool._id,
                pool: pool.rootPool,
                contractAddress: pool?.contractAddress,
                bondAmount: pool.stakingAmount,
                createdAt: pool.createdAt,
                updatedAt: pool.updatedAt,
                // ...pool,
            })) || [];
            // console.log(transformedData, 'transformedDatatransformedData')
            setChildPools(transformedData);
            setIsLoading(false);
            return {
                success: true,
                data: transformedData,
                total: result.data?.pagination?.total || 0
            };

        } catch (error) {
            console.error('Failed to fetch child pools:', error);
            setError(error.message);
            setIsLoading(false);
            return { success: false, error: error.message };
        }
    }, []);

    return {
        childPools,
        isLoading,
        error,
        pagination,
        fetchChildPools,
    };
};

export default useChildPoolsAPI;